import { useRouter } from 'next/router'

import clsx from 'clsx'
import useSWR from 'swr'

import { serializeCacheKey } from '@/lib/contentful/utils'

import { isDevelopment } from '@/utils/isDevelopment'

import { Link } from '@/components/CustomLink'
import { Warning } from '@/components/Warning'

import { getGlossaryCategories } from '../helpers/getGlossaryCategories'

type Props = {
	className?: string
}

export function GlossarySidebar({ className }: Props) {
	const { locale, asPath, push } = useRouter()

	const { data } = useSWR(
		serializeCacheKey('glossaryCategories', { limit: 100, locale }),
		async () => await getGlossaryCategories(0, 100, locale)
	)

	const categories = data?.sort((a, b) => {
		if (a.slug === 'glossary') return -1
		if (b.slug === 'glossary') return 1
		return 0
	})

	if (!categories) {
		if (isDevelopment()) {
			return <Warning>GlossarySidebar: categories is undefined</Warning>
		}

		return null
	}

	return (
		<>
			<ul className={clsx('space-y-3 max-md:hidden', className)}>
				{categories.map((category, i, arr) => {
					if (!category?.slug || !category.name) return null
					const href = `/${category.slug !== 'glossary' ? 'glossary/' : ''}${category.slug}/`

					return (
						<li key={category.slug}>
							<Link
								href={href}
								className={clsx(
									'py-1 inline-flex border-b-2 text-gray-500 hover:text-black-100',
									i === 0 && 'pt-0',
									i === arr.length - 1 && 'pb-0',
									asPath.split(/[?#]/)[0] === href ? 'border-bc-blue' : 'border-transparent'
								)}
								data-testid={
									asPath.split(/[?#]/)[0] === href
										? 'glossary-sidebar-item-active'
										: 'glossary-sidebar-item'
								}
							>
								{category.name === 'Glossary' ? 'Home' : category.name}
							</Link>
						</li>
					)
				})}
			</ul>

			<div className={clsx('md:hidden', className)}>
				<select
					className="w-full border-gray-1000 bg-transparent rounded-sm"
					value={asPath}
					onChange={(event) => push(event.target.value)}
				>
					{categories?.map((category, i) => {
						if (!category?.slug || !category.name) return null

						const href = `/${category.slug !== 'glossary' ? 'glossary/' : ''}${category.slug}/`

						return (
							<option key={i} value={href}>
								{category.name === 'Glossary' ? 'Home' : category.name}
							</option>
						)
					})}
				</select>
			</div>
		</>
	)
}
